// Saved layout of the historic tickets grid, shared by browse and search mode.
// Kept free of React, DevExtreme and path aliases so Node can load it in the checks.
//
// One in-memory store per page is the source of truth for the browse layout (columns, grouping,
// filters, paging). Storage is only its persistent copy: it is read once and written best-effort.
// Before every change of search, the page captures the grid's current state into the store, so
// edits that DevExtreme has not saved yet (it saves after a delay and drops the pending save when
// the grid is replaced) are not lost.
//
// Search mode works ungrouped on its own first page, and never overwrites the browse grouping or
// paging with that search-only layout.

export const HISTORIC_GRID_STORAGE_KEY = 'tickets-historic-datagrid'
export const SEARCH_PAGE_SIZE = 20

export type GridColumnState = Record<string, unknown>
export type GridState = Record<string, unknown> & { columns?: GridColumnState[] }
export type GridMode = 'browse' | 'search'

/** The subset of Storage used here; `localStorage` satisfies it. */
export type StateStorage = Pick<Storage, 'getItem' | 'setItem'>

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Returns a deep copy of a layout the grid can safely apply, or `undefined`.
 * The copy goes through JSON, as the native localStorage mode does, so dates and filters are
 * handled the same way. A `columns` value that is not a list of objects is dropped (the grid would
 * fail on it), and the rest of the layout is kept.
 */
export const sanitizeGridState = (value: unknown): GridState | undefined => {
  if (!isPlainObject(value)) return undefined
  let copy: unknown
  try {
    copy = JSON.parse(JSON.stringify(value))
  } catch {
    return undefined
  }
  if (!isPlainObject(copy)) return undefined
  if ('columns' in copy) {
    const { columns } = copy
    if (!Array.isArray(columns) || !columns.every((column) => isPlainObject(column))) delete copy.columns
  }
  return copy as GridState
}

/** Reads the stored layout. Missing, unreadable, malformed or invalid data yields `undefined`. */
export const readGridState = (storage: StateStorage | undefined, key: string): GridState | undefined => {
  try {
    const raw = storage?.getItem(key)
    if (typeof raw !== 'string') return undefined
    return sanitizeGridState(JSON.parse(raw))
  } catch {
    return undefined
  }
}

/** Writes the layout as the native localStorage mode does. Storage failures are ignored. */
export const writeGridState = (storage: StateStorage | undefined, key: string, state: unknown): void => {
  try {
    storage?.setItem(key, JSON.stringify(state))
  } catch {
    // Quota or private-mode errors must not break the grid; the native mode only logs them.
  }
}

const withoutGroupIndex = (column: GridColumnState): GridColumnState => {
  if (!('groupIndex' in column)) return column
  const rest = { ...column }
  delete rest.groupIndex
  return rest
}

/** Layout for search mode: no grouping, first page, search page size. Everything else is kept. */
export const toSearchState = (state: GridState | undefined): GridState | undefined => {
  const safe = sanitizeGridState(state)
  if (!safe) return undefined
  const next: GridState = { ...safe, pageIndex: 0, pageSize: SEARCH_PAGE_SIZE }
  if (safe.columns) next.columns = safe.columns.map(withoutGroupIndex)
  return next
}

const columnKey = (column: GridColumnState): string | undefined => {
  if (typeof column.dataField === 'string') return `dataField:${column.dataField}`
  if (typeof column.name === 'string') return `name:${column.name}`
  return undefined
}

/**
 * Folds a search-mode layout into the browse layout: the search session's column changes (order,
 * width, visibility, filters) are kept, but grouping and paging come from the browse layout.
 * Columns are matched by data field or name; an unmatched column gets no grouping.
 */
export const mergeSearchStateIntoBrowse = (searchState: GridState, browseState: GridState | undefined): GridState => {
  const next: GridState = { ...searchState }
  delete next.pageIndex
  delete next.pageSize
  if (browseState && 'pageIndex' in browseState) next.pageIndex = browseState.pageIndex
  if (browseState && 'pageSize' in browseState) next.pageSize = browseState.pageSize

  if (searchState.columns) {
    const browseGroups = new Map<string, unknown>()
    for (const column of browseState?.columns ?? []) {
      const key = columnKey(column)
      if (key && column.groupIndex !== undefined) browseGroups.set(key, column.groupIndex)
    }
    next.columns = searchState.columns.map((column) => {
      const rest = { ...withoutGroupIndex(column) }
      const key = columnKey(column)
      if (key && browseGroups.has(key)) rest.groupIndex = browseGroups.get(key)
      return rest
    })
  }
  return next
}

/**
 * The page's single source of truth for the browse layout. Storage is read once, on first use,
 * and every update is written back best-effort.
 */
export const createGridLayoutStore = (storage: StateStorage | undefined, key: string) => {
  let loaded = false
  let browse: GridState | undefined

  const current = (): GridState | undefined => {
    if (!loaded) {
      browse = readGridState(storage, key)
      loaded = true
    }
    return browse
  }

  const update = (next: GridState) => {
    browse = next
    loaded = true
    writeGridState(storage, key, next)
  }

  return {
    /** Layout to apply when a grid of this mode is created. Always a fresh copy. */
    load(mode: GridMode): GridState | undefined {
      const browseState = current()
      return mode === 'search' ? toSearchState(browseState) : sanitizeGridState(browseState)
    },
    /**
     * Records a state reported by the grid in the given mode: from its save callback, or captured
     * just before the grid is replaced. A state without a column list (the grid has not applied
     * its layout yet) is ignored, so it can never erase the browse layout.
     */
    save(mode: GridMode, state: unknown) {
      const safe = sanitizeGridState(state)
      if (!safe?.columns) return
      update(mode === 'browse' ? safe : mergeSearchStateIntoBrowse(safe, current()))
    },
  }
}

export type GridLayoutStore = ReturnType<typeof createGridLayoutStore>

/**
 * customLoad/customSave for the grid's StateStoring, for one mode, backed by the layout store.
 * `isActive` (optional) tells whether this grid instance is still the page's current grid: a
 * replaced grid's delayed save is then ignored, so it cannot overwrite newer filters or layout.
 */
export const createGridStateStoring = (
  mode: GridMode,
  store: GridLayoutStore,
  isActive: () => boolean = () => true,
) => ({
  customLoad: (): Promise<GridState | undefined> => Promise.resolve(store.load(mode)),
  customSave: (state: GridState): void => {
    if (isActive()) store.save(mode, state)
  },
})
